/**
 * Multi-Variant Images
 * Handles showing/hiding variant-specific images based on alt text tags
 * Tag format: [variant:VariantTitle] in image alt text
 */

class MultiVariantImages {
  constructor(mediaGallery) {
    this.mediaGallery = mediaGallery;
    this.enabled = mediaGallery.dataset.enableMultiVariantImages === 'true';

    if (!this.enabled) return;

    this.currentVariant = mediaGallery.dataset.currentVariant || '';

    // Cache DOM queries for performance (query once, reuse many times)
    this.cachedMediaItems = null;
    this.cachedThumbnails = null;

    this.init();
  }

  init() {
    // Cache DOM elements on initialization
    this.cacheElements();

    // On page load, ensure the first visible image is active
    // This handles the case where URL has a variant parameter
    this.initializeActiveImage();

    // Listen for variant changes from custom variant picker
    document.addEventListener('variant:changed', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateImagesForVariant(event.detail.variant);
      }
    });
  }

  /**
   * Initialize the active image on page load
   * Ensures first visible image is set as active, regardless of which variant loads
   */
  initializeActiveImage() {
    // Use requestAnimationFrame to ensure Shopify's media gallery has initialized
    requestAnimationFrame(() => {
      // Find the first visible media item
      const firstVisibleMedia = Array.from(this.cachedMediaItems).find((item) => !item.classList.contains('hidden'));

      if (firstVisibleMedia) {
        const mediaId = firstVisibleMedia.dataset.mediaId;
        // Only set active if it's not already active (prevents unnecessary reloading)
        if (
          mediaId &&
          typeof this.mediaGallery.setActiveMedia === 'function' &&
          !firstVisibleMedia.classList.contains('is-active')
        ) {
          // Set as active WITH prepending to ensure it's in first position
          // This triggers Shopify's first-image display behavior
          this.mediaGallery.setActiveMedia(mediaId, true);
        }
      }
    });
  }

  /**
   * Cache DOM elements once to avoid repeated queries
   */
  cacheElements() {
    this.cachedMediaItems = this.mediaGallery.querySelectorAll('.product__media-item');
    this.cachedThumbnails = this.mediaGallery.querySelectorAll('.thumbnail-list__item');
  }

  /**
   * Update visible images based on selected variant
   * @param {Object} variant - The selected variant object with title property
   */
  updateImagesForVariant(variant) {
    if (!variant || !variant.title) return;

    const variantTitle = variant.title.toLowerCase();
    this.currentVariant = variantTitle;

    // Update data attribute for current variant
    this.mediaGallery.dataset.currentVariant = variant.title;

    let firstVisibleMediaId = null;

    // Filter media items - using cached elements for performance
    this.cachedMediaItems.forEach((item) => {
      const variantTag = item.dataset.variantTag;
      const shouldShow = this.shouldShowForVariant(variantTag, variantTitle);

      if (shouldShow) {
        item.classList.remove('hidden');
        // Track first visible image
        if (!firstVisibleMediaId) {
          firstVisibleMediaId = item.dataset.mediaId;
        }
      } else {
        item.classList.add('hidden');
      }
    });

    // Filter thumbnails - using cached elements for performance
    this.cachedThumbnails.forEach((thumb) => {
      const variantTag = thumb.dataset.variantTag;
      const shouldShow = this.shouldShowForVariant(variantTag, variantTitle);

      if (shouldShow) {
        thumb.classList.remove('hidden');
      } else {
        thumb.classList.add('hidden');
      }
    });

    // Set the first visible image as active and move it to first position
    // prepend: true moves the element to first position, triggering first-image display behavior
    // Only set if not already active to prevent unnecessary reloading
    if (firstVisibleMediaId && typeof this.mediaGallery.setActiveMedia === 'function') {
      const firstVisibleMedia = Array.from(this.cachedMediaItems).find(
        (item) => item.dataset.mediaId === firstVisibleMediaId
      );
      if (firstVisibleMedia && !firstVisibleMedia.classList.contains('is-active')) {
        this.mediaGallery.setActiveMedia(firstVisibleMediaId, true);
      }
    }

    // Update slider counter to reflect visible media count
    this.updateSliderCounter();
  }

  /**
   * Determine if media should show for the given variant
   * @param {string} variantTag - The tag from the media's data attribute
   * @param {string} variantTitle - The current variant title (lowercase)
   * @returns {boolean} - Whether the media should be visible
   */
  shouldShowForVariant(variantTag, variantTitle) {
    // No tag or "all" tag always shows
    if (!variantTag || variantTag === 'all') {
      return true;
    }

    // Compare tags (case-insensitive)
    return variantTag.toLowerCase() === variantTitle;
  }

  /**
   * Update the slider counter to reflect visible media count
   */
  updateSliderCounter() {
    // Count visible items from cached elements (faster than re-querying)
    let visibleCount = 0;
    this.cachedMediaItems.forEach((item) => {
      if (!item.classList.contains('hidden')) {
        visibleCount++;
      }
    });

    const counterTotal = this.mediaGallery.querySelector('.slider-counter--total');

    if (counterTotal) {
      counterTotal.textContent = visibleCount;
    }
  }

  /**
   * Get variant tag from image alt text
   * @param {string} altText - The alt text to parse
   * @returns {string|null} - The variant tag or null
   */
  static parseVariantTag(altText) {
    if (!altText) return null;

    const match = altText.match(/\[variant:(.*?)\]/i);
    return match ? match[1].trim().toLowerCase() : null;
  }
}

// Initialize for all media galleries on the page
document.addEventListener('DOMContentLoaded', () => {
  const mediaGalleries = document.querySelectorAll('media-gallery[data-enable-multi-variant-images]');

  mediaGalleries.forEach((gallery) => {
    new MultiVariantImages(gallery);
  });
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MultiVariantImages;
}
